import { isEqual, isString } from 'lodash-es';
import { of } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';

import { AfterViewInit, ChangeDetectionStrategy, Component, Input, Optional } from '@angular/core';
import type { AbstractControlDirective, ControlContainer, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { AbstractControl, FormGroupDirective } from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';

import { Destroyable, takeUntilDestroyed } from '@bp/shared/models/common';
import { OptionalBehaviorSubject } from '@bp/shared/rxjs';
import { OnChanges, SimpleChanges } from '@bp/shared/models/core';

@Component({
	// eslint-disable-next-line @angular-eslint/component-selector
	selector: '[bpFieldError]',
	templateUrl: './field-error.component.html',
	styleUrls: [ './field-error.component.scss' ],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldErrorComponent extends Destroyable implements OnChanges, AfterViewInit {

	@Input('bpFieldError') formControlOrName?: AbstractControl | string;

	@Input('bpFieldErrorSuppress') suppress?: boolean | '' | null = false;

	@Input('bpFieldErrorAnimate') animate = false;

	errors$ = new OptionalBehaviorSubject<ValidationErrors | null>();

	formControlName$ = new OptionalBehaviorSubject<number | string | null>();

	private readonly _formControl$ = new OptionalBehaviorSubject<AbstractControl | AbstractControlDirective | null>();

	private get _form(): UntypedFormGroup | undefined {
		return this._formGroupDirective?.form;
	}

	constructor(
		@Optional() private readonly _formField?: MatFormField,
		@Optional() private readonly _formGroupDirective?: FormGroupDirective,
	) {
		super();

		this._emitErrorsOnFormControlErrorStatusChange();
	}

	ngOnChanges({ formControlOrName }: SimpleChanges<this>): void {
		if (formControlOrName && this.formControlOrName)
		 	this._updateFormControlBasedOnInput();
	}

	ngAfterViewInit(): void {
		this._formField && this._updateFormControlBasedOnInjectedFormField();
	}

	private _emitErrorsOnFormControlErrorStatusChange(): void {
		this._formControl$
			.pipe(
				switchMap(formControl => formControl?.statusChanges
					// @ts-expect-error TODO remove on ts 4.6.0, some weird error with type inference
					? formControl.statusChanges.pipe(map(() => formControl.errors))
					: of(null)),
				distinctUntilChanged(isEqual),
				takeUntilDestroyed(this),
			)
			.subscribe(errors => void this.errors$.next(errors));
	}

	private _updateFormControlBasedOnInput(): void {
		if (this.formControlOrName instanceof AbstractControl) {
			this._formControl$.next(this.formControlOrName);

			if (this._form)
				this._findControlNameAndPushToFormControlNameStream();

		} else {
			this._formControl$.next(this._form?.controls[this.formControlOrName!] ?? null);

			this.formControlName$.next(this.formControlOrName!);
		}

	}

	private _updateFormControlBasedOnInjectedFormField(): void {
		const ngControl = <ControlContainer> this._formField!._control.ngControl;

		this._formControl$.next(ngControl.control);

		if (!isString(this.formControlOrName))
			this.formControlName$.next(ngControl.name);
	}

	private _findControlNameAndPushToFormControlNameStream(): void {
		const [ formControlName ] = Object.entries(this._form!.controls)
			.find(([ ,control ]) => control === this.formControlOrName) ?? [];

		if (formControlName)
			this.formControlName$.next(formControlName);
	}
}
